import type { getCouponUpdateState } from "features/subscriptions/utils";

import type {
  RentedSubscription,
  WorkspaceAccount,
  WorkspaceLicense,
} from "common/types";

type CouponUpdateState = ReturnType<typeof getCouponUpdateState>;

export function getRentalAccountsForSub(
  accounts: WorkspaceAccount[],
  subscriptionId: string,
): WorkspaceAccount[] {
  return accounts.filter(
    ({ type, subscription_id }) =>
      type === "rental" && subscription_id === subscriptionId,
  );
}

export function getRequiredAccountIdsCountForDowngrade(
  sub: RentedSubscription,
  targetCount: number,
  accounts: WorkspaceAccount[],
): number {
  const assignedCount = getRentalAccountsForSub(accounts, sub.id).length;

  return Math.max(0, assignedCount - targetCount);
}

export function getAssignedRentalAccountCount(
  licenses: WorkspaceLicense[],
): number {
  return licenses.filter(
    ({ source, account_id }) => source === "rental" && !!account_id,
  ).length;
}

// Only include fields that changed — backend requires count or item_price_id.
// Estimates always include count so they can be requested without changes
// (needed to read the currently applied coupon on dialog open)
export function buildRentalUpdateRequestBody({
  currentCount,
  newCount,
  currentItemPriceId,
  newItemPriceId,
  accountIds,
  couponUpdateState,
  isEstimate = false,
}: {
  currentCount: number;
  newCount: number;
  currentItemPriceId: string;
  newItemPriceId: string | undefined;
  accountIds?: string[];
  couponUpdateState?: CouponUpdateState;
  isEstimate?: boolean;
}): Record<string, unknown> | null {
  const body: Record<string, unknown> = {};

  if (isEstimate || newCount !== currentCount) {
    body.count = newCount;
  }

  if (newItemPriceId && newItemPriceId !== currentItemPriceId) {
    body.item_price_id = newItemPriceId;
  }

  if (!("count" in body) && !("item_price_id" in body)) {
    return null;
  }

  if (accountIds?.length) {
    body.account_ids = accountIds;
  }

  if (couponUpdateState) {
    Object.assign(body, {
      coupon: couponUpdateState.coupon,
      remove_coupon: couponUpdateState.removeCoupon,
    });
  }

  return body;
}
